import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const MENUS = [
  {
    label: 'File',
    items: [
      { label: 'New Journal Entry', path: '/journal/new' },
      { label: 'New Invoice', path: '/invoices/new' },
      { label: 'New Bill', path: '/bills/new' },
      { separator: true },
      { label: 'Backup Database', path: '/settings/backup' },
      { label: 'Exit', action: 'logout' },
    ],
  },
  {
    label: 'Edit',
    items: [
      { label: 'Undo', shortcut: 'Ctrl+Z' },
      { label: 'Redo', shortcut: 'Ctrl+Y' },
      { separator: true },
      { label: 'Cut', shortcut: 'Ctrl+X' },
      { label: 'Copy', shortcut: 'Ctrl+C' },
      { label: 'Paste', shortcut: 'Ctrl+V' },
    ],
  },
  {
    label: 'View',
    items: [
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'Reports', path: '/reports' },
      { label: 'Audit Trail', path: '/settings/audit' },
      { separator: true },
      { label: 'Recurring Schedules', path: '/recurring' },
      { label: 'Company Calendar', path: '/settings/fiscal' },
    ],
  },
  {
    label: 'Lists',
    items: [
      { label: 'Chart of Accounts', path: '/accounts' },
      { label: 'Customers', path: '/customers' },
      { label: 'Vendors', path: '/vendors' },
      { label: 'Employees', path: '/employees' },
      { label: 'Products & Services', path: '/products' },
      { label: 'Fixed Assets', path: '/assets' },
      { label: 'Users', path: '/settings/users' },
    ],
  },
  {
    label: 'Favorites',
    items: [
      { label: 'No favorites yet', disabled: true },
      { separator: true },
      { label: 'Add Current Page to Favorites', disabled: true },
    ],
  },
  {
    label: 'Sales',
    items: [
      { label: 'Customers', path: '/customers' },
      { label: 'Estimates', path: '/estimates' },
      { label: 'Sales Orders', path: '/sales-orders' },
      { label: 'Invoices', path: '/invoices' },
      { label: 'Receive Payments', path: '/payments' },
    ],
  },
  {
    label: 'Company',
    items: [
      { label: 'Company Profile', path: '/settings/company' },
      { label: 'Theme & Branding', path: '/settings/branding' },
      { label: 'Currencies & Exchange Rates', path: '/settings/currency' },
      { label: 'Users & Permissions', path: '/settings/users' },
      { label: 'Tax Settings', path: '/tax' },
      { label: 'Fiscal Year / Calendar', path: '/settings/fiscal' },
      { label: 'Backup & Recovery', path: '/settings/backup' },
    ],
  },
  {
    label: 'Subscription',
    items: [
      { label: 'My Subscription', path: '/subscription' },
      { label: 'Plan Details', path: '/subscription' },
    ],
  },
  {
    label: 'Customers',
    items: [
      { label: 'Customer List', path: '/customers' },
      { label: 'New Customer', path: '/customers/new' },
      { separator: true },
      { label: 'Invoices', path: '/invoices' },
      { label: 'Customer Statements', path: '/customers' },
    ],
  },
  {
    label: 'Vendors',
    items: [
      { label: 'Vendor List', path: '/vendors' },
      { label: 'New Vendor', path: '/vendors/new' },
      { separator: true },
      { label: 'Bills', path: '/bills' },
      { label: 'Purchase Orders', path: '/purchase-orders' },
    ],
  },
  {
    label: 'Employees',
    items: [
      { label: 'Employee Center', path: '/employees' },
      { label: 'Payroll', path: '/payroll' },
      { label: 'Recurring Payroll', path: '/recurring' },
      { label: 'Time Tracking', path: '/employees' },
    ],
  },
  {
    label: 'Banking',
    items: [
      { label: 'Bank Accounts', path: '/banking' },
      { label: 'Transactions', path: '/banking/transactions' },
      { label: 'Reconciliation', path: '/banking/reconciliation' },
      { label: 'Transfers', path: '/banking' },
    ],
  },
  {
    label: 'Reports',
    items: [
      { label: 'Report Center', path: '/reports' },
      { separator: true },
      { label: 'Profit & Loss', path: '/reports' },
      { label: 'Balance Sheet', path: '/reports' },
      { label: 'Trial Balance', path: '/reports' },
      { label: 'General Ledger', path: '/reports' },
    ],
  },
  {
    label: 'Window',
    items: [
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'New Window', action: 'newwindow' },
    ],
  },
  {
    label: 'Help',
    items: [
      { label: 'About LedgerPro', action: 'about' },
      { label: 'Documentation', action: 'about' },
    ],
  },
];

/**
 * Traditional desktop menu bar. Each top-level item opens a dropdown on
 * click; arrow keys + Enter navigate within the open menu, Esc closes.
 */
export default function MainMenu({ onLogout }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Platform super admin has no company_id — but MainMenu is only used by company users
  // So this will always be false for company users. Keep the check for safety.
  const isPlatformAdmin = !user?.company_id && user?.role_name === 'Administrator';
  const [open, setOpen] = useState(null);
  const [highlight, setHighlight] = useState(0);
  const barRef = useRef(null);

  // Build menus: Subscription for all, Platform Admin for admin only
  const menus = MENUS.map(m => {
    if (m.label === 'Subscription' && isPlatformAdmin) {
      return {
        ...m,
        items: [
          ...m.items,
          { separator: true },
          { label: 'Platform Admin', path: '/super-admin' },
        ],
      };
    }
    return m;
  });

  useEffect(() => {
    const onDocClick = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) setOpen(null);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (open !== null) setHighlight(0);
  }, [open]);

  const run = (item) => {
    setOpen(null);
    if (item.action === 'logout') return onLogout();
    if (item.action === 'newwindow') return window.open(window.location.href, '_blank');
    if (item.path) return navigate(item.path);
    if (item.disabled) return;
    window.alert(`"${item.label}" (demo action — connected in the accounting module)`);
  };

  const onKeyDown = (e, idx) => {
    if (e.key === 'Escape') { setOpen(null); return; }
    if (e.key === 'ArrowRight') { setOpen((open + 1) % menus.length); return; }
    if (e.key === 'ArrowLeft') { setOpen((open - 1 + menus.length) % menus.length); return; }
    if (e.key === 'ArrowDown' && open === idx) { setHighlight(h => Math.min(h + 1, menus[idx].items.length - 1)); return; }
    if (e.key === 'ArrowUp' && open === idx) { setHighlight(h => Math.max(h - 1, 0)); return; }
    if (e.key === 'Enter' && open === idx) {
      const items = menus[idx].items;
      const target = items[highlight];
      if (target) run(target);
    }
  };

  return (
    <nav className="desk-menubar" ref={barRef} role="menubar">
      {menus.map((menu, idx) => (
        <div className="desk-menu" key={menu.label} role="none">
          <button
            type="button"
            role="menuitem"
            className={`desk-menu-btn ${open === idx ? 'open' : ''}`}
            onMouseEnter={() => { if (open !== null) setOpen(idx); }}
            onClick={() => setOpen(open === idx ? null : idx)}
            onKeyDown={(e) => onKeyDown(e, idx)}
          >
            {menu.label}
          </button>
          {open === idx && (
            <div className="desk-menu-dropdown" role="menu">
              {menu.items.map((item, i) =>
                item.separator ? (
                  <div className="desk-menu-sep" key={`sep-${i}`} />
                ) : (
                  <button
                    type="button"
                    key={item.label}
                    role="menuitem"
                    className={`desk-menu-item ${i === highlight ? 'open' : ''}`}
                    style={i === highlight ? { background: 'var(--desk-blue-soft)', color: 'var(--desk-blue)' } : undefined}
                    disabled={item.disabled}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => run(item)}
                  >
                    <span className="desk-menu-label">{item.label}</span>
                    {item.shortcut && <span className="desk-menu-shortcut">{item.shortcut}</span>}
                  </button>
                )
              )}
            </div>
          )}
        </div>
      ))}
    </nav>
  );
}
